// ABOUTME: Pure helpers for the sponsored-link platform: record normalization,
// ABOUTME: input validation, scheduling, and weighted rotation. No Firebase here.

/** A sponsored link as stored in Firestore `ads/{id}` and consumed by the app. */
export interface AdRecord {
  id: string;
  /** Headline shown on the card. */
  title: string;
  /** Absolute https:// destination. */
  url: string;
  /** One-line supporting copy shown under the title. */
  description: string;
  /** Who paid for it — shown to admins, not to users. */
  sponsor: string;
  /** Inactive ads are kept for the record but never rotated in. */
  active: boolean;
  /** Relative share of rotations, 1–10. Two ads at 3 and 1 show 75% / 25%. */
  weight: number;
  /** Optional flight window, epoch ms. Outside it the ad is not live. */
  startAt: number | null;
  endAt: number | null;
  createdAt: number | null;
  updatedAt: number | null;
}

/** The fields an admin edits. Everything else is derived or server-stamped. */
export type AdInput = Pick<
  AdRecord,
  "title" | "url" | "description" | "sponsor" | "active" | "weight" | "startAt" | "endAt"
>;

export const AD_LIMITS = {
  titleMax: 80,
  descriptionMax: 140,
  sponsorMax: 80,
  urlMax: 2048,
  weightMin: 1,
  weightMax: 10,
} as const;

export const EMPTY_AD_INPUT: AdInput = {
  title: "",
  url: "",
  description: "",
  sponsor: "",
  active: true,
  weight: 1,
  startAt: null,
  endAt: null,
};

/**
 * Coerce the many shapes an `ads` document can have into one record.
 * Pre-revamp documents only had title/url/description; they read as active,
 * weight 1, unscheduled — exactly how they behaved before.
 */
export function normalizeAdRecord(id: string, data: Record<string, unknown>): AdRecord {
  const str = (v: unknown) => (typeof v === "string" ? v : "");
  const millis = (v: unknown): number | null => {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (v && typeof (v as { toMillis?: unknown }).toMillis === "function") {
      return (v as { toMillis: () => number }).toMillis();
    }
    return null;
  };
  const weight = typeof data.weight === "number" ? data.weight : AD_LIMITS.weightMin;
  return {
    id,
    title: str(data.title),
    url: str(data.url),
    description: str(data.description),
    sponsor: str(data.sponsor),
    active: data.active !== false,
    weight: clampWeight(weight),
    startAt: millis(data.startAt),
    endAt: millis(data.endAt),
    createdAt: millis(data.createdAt),
    updatedAt: millis(data.updatedAt),
  };
}

export function clampWeight(weight: number): number {
  if (!Number.isFinite(weight)) return AD_LIMITS.weightMin;
  return Math.min(AD_LIMITS.weightMax, Math.max(AD_LIMITS.weightMin, Math.round(weight)));
}

/** Prefix a bare domain with https:// and trim; leaves other schemes alone so validation can reject them. */
export function normalizeAdUrl(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === "") return "";
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  return `https://${trimmed.replace(/^\/+/, "")}`;
}

export type AdFieldErrors = Partial<Record<keyof AdInput, string>>;

/**
 * Validate an admin's edits. Returns a map of field → message; an empty map
 * means the input is clean. Mirrors the Firestore rule so a rejected write
 * never surprises the admin.
 */
export function validateAdInput(input: AdInput): AdFieldErrors {
  const errors: AdFieldErrors = {};
  const title = input.title.trim();
  if (title === "") errors.title = "Title is required.";
  else if (title.length > AD_LIMITS.titleMax)
    errors.title = `Keep the title under ${AD_LIMITS.titleMax} characters.`;

  const url = normalizeAdUrl(input.url);
  if (url === "") errors.url = "A destination URL is required.";
  else if (url.length > AD_LIMITS.urlMax) errors.url = "That URL is too long.";
  else {
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== "https:") errors.url = "Sponsored links must use https://.";
      else if (!parsed.hostname.includes("."))
        errors.url = "That doesn't look like a public hostname.";
    } catch {
      errors.url = "That isn't a valid URL.";
    }
  }

  if (input.description.length > AD_LIMITS.descriptionMax) {
    errors.description = `Keep the description under ${AD_LIMITS.descriptionMax} characters.`;
  }
  if (input.sponsor.length > AD_LIMITS.sponsorMax) {
    errors.sponsor = `Keep the sponsor name under ${AD_LIMITS.sponsorMax} characters.`;
  }
  if (
    !Number.isInteger(input.weight) ||
    input.weight < AD_LIMITS.weightMin ||
    input.weight > AD_LIMITS.weightMax
  ) {
    errors.weight = `Weight must be a whole number from ${AD_LIMITS.weightMin} to ${AD_LIMITS.weightMax}.`;
  }
  if (input.startAt !== null && input.endAt !== null && input.endAt <= input.startAt) {
    errors.endAt = "The end date must be after the start date.";
  }
  return errors;
}

/** Trim and normalize edits into exactly what gets written. */
export function prepareAdInput(input: AdInput): AdInput {
  return {
    title: input.title.trim(),
    url: normalizeAdUrl(input.url),
    description: input.description.trim(),
    sponsor: input.sponsor.trim(),
    active: input.active,
    weight: clampWeight(input.weight),
    startAt: input.startAt,
    endAt: input.endAt,
  };
}

/** Active, inside its flight window (if any), and pointing somewhere. */
export function isAdLive(ad: AdRecord, now: number = Date.now()): boolean {
  if (!ad.active) return false;
  if (!ad.url || !ad.title) return false;
  if (ad.startAt !== null && now < ad.startAt) return false;
  if (ad.endAt !== null && now >= ad.endAt) return false;
  return true;
}

export type AdStatus = "live" | "paused" | "scheduled" | "ended" | "incomplete";

/** Human-facing state for the admin list. */
export function getAdStatus(ad: AdRecord, now: number = Date.now()): AdStatus {
  if (!ad.url || !ad.title) return "incomplete";
  if (!ad.active) return "paused";
  if (ad.startAt !== null && now < ad.startAt) return "scheduled";
  if (ad.endAt !== null && now >= ad.endAt) return "ended";
  return "live";
}

/**
 * Weighted random pick among live ads. `random` is injectable for tests and
 * must return a number in [0, 1). Returns null when nothing is live.
 *
 * `excludeId` is the ad shown last time. A start page is reloaded many times
 * a day, and a memoryless roll re-showed the same card run after run; with
 * two or more live ads the previous one sits this roll out. With only one
 * live ad there is nothing else to show, so it is not excluded.
 */
export function pickWeightedAd(
  ads: AdRecord[],
  now: number = Date.now(),
  random: () => number = Math.random,
  excludeId: string | null = null
): AdRecord | null {
  let live = ads.filter((ad) => isAdLive(ad, now));
  if (excludeId !== null && live.length > 1) {
    live = live.filter((ad) => ad.id !== excludeId);
  }
  if (live.length === 0) return null;
  const total = live.reduce((sum, ad) => sum + ad.weight, 0);
  let roll = random() * total;
  for (const ad of live) {
    roll -= ad.weight;
    if (roll < 0) return ad;
  }
  return live[live.length - 1];
}

/** Each live ad's expected share of rotations, for the admin readout. */
export function rotationShares(ads: AdRecord[], now: number = Date.now()): Map<string, number> {
  const live = ads.filter((ad) => isAdLive(ad, now));
  const total = live.reduce((sum, ad) => sum + ad.weight, 0);
  const shares = new Map<string, number>();
  for (const ad of live) shares.set(ad.id, total > 0 ? ad.weight / total : 0);
  return shares;
}
